/**
 * @file ExtractedInfoPage.tsx
 *
 * @description A React component that displays extracted information from incident reports.
 * This page allows police officers to review, edit, and submit incident information to RMS.
 *
 * @module Views
 * @requires React
 * @requires Material-UI
 */
import {
  Alert,
  Box,
  Button,
  Skeleton,
  alpha,
  useTheme,
  Container,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PreviewIcon from "@mui/icons-material/Preview";
import { usePageHeader } from "../../context/LayoutContext";
import { ExtractedInformation } from "./ExtractedInformation";
import * as JsonRefs from "json-refs";
import { EforceIncidentModel } from "../../shared/interfaces/EforceModels";
import { createRef, useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { VideoData } from "../../shared/interfaces/VideoData";
import { useToast } from "../../shared/toast";
import { Download } from "@mui/icons-material";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import {
  downloadCombinedTranscriptInLanguage,
  hasAnyTranscript,
} from "../../utils/transcriptDownload";
import { DownloadTranscriptLanguageModal } from "../../modals/DownloadTranscriptLanguageModal";
import { saveAs } from "file-saver";
import { ReportSubmitModal } from "../../modals/ReportSubmitModal";
import { VideoPreview } from "../../components/video/VideoPreview";
import { DownloadReportModal } from "../../modals/DownloadReportModal";
import { ExtractedInformationSkeleton } from "../../components/skeletons/ExtractedInformationSkeleton";
import type { JSONSchema7 } from "json-schema";
import { FieldMapping } from "../../config/fieldMapping";
import { eforceSchemaMapping } from "../../config/eforceSchemaMapping";
import { genericSchemaMapping } from "../../config/genericSchemaMapping";
import {
  getEvidenceData,
  getIncidentData,
  getReportMetadata,
  rmsCommitReport,
} from "../../api/reportApi";
import { rmsClient } from "../../api/clients";
import { filterSchemaByData } from "../../utils/filterSchemaByData";
import { ReportStatus } from "../../shared/interfaces/ReportStatus";
import { usePermissions } from "../../hooks/usePermissions";
import { ReviewHistoryDialog } from "../../components/coReview/ReviewHistoryDialog";
import HistoryIcon from "@mui/icons-material/History";
import { REPORTS_MANAGE_STATION_PERMISSION } from "../../shared/auth/authorization";
import { useCoApprovalEnabled } from "../../hooks/useCoApprovalEnabled";
import { EDITABLE_STATUSES } from "../EditSection/hooks/useReportData";

// Statuses where the officer has already signed and the report has moved past
// the editable draft phase. CHANGES_REQUESTED is intentionally excluded — the
// officer needs to re-sign after addressing CO feedback.
const SIGNED_REPORT_STATUSES: readonly ReportStatus[] = [
  ReportStatus.SIGNED,
  ReportStatus.IN_CO_REVIEW,
  ReportStatus.CO_APPROVED,
  ReportStatus.RMS_COMMITTED,
];

// Statuses the edit page accepts. Auto-redirecting to the edit page for any
// other status would bounce straight back here in a loop, so this reuses the
// edit page's own whitelist.
const EDIT_REDIRECT_STATUSES: readonly ReportStatus[] = EDITABLE_STATUSES;

// Statuses for which a CO review-history entry exists — anything that has
// entered or completed the CO workflow. Used to gate the "Review history"
// button; still requires the CO feature flag to be enabled.
const REVIEW_HISTORY_VISIBLE_STATUSES: readonly ReportStatus[] = [
  ReportStatus.IN_CO_REVIEW,
  ReportStatus.CO_APPROVED,
  ReportStatus.CHANGES_REQUESTED,
  ReportStatus.RMS_COMMITTED,
];

/**
 * Props for the ExtractedInfoPage component
 * @interface ExtractedInfoPageProps
 * @property {boolean} [isFinalReview] - Indicates if this is the final review stage
 * @property {React.MutableRefObject<EforceIncidentModel | null> | undefined} [incidentModelRef] - Reference to the incident model
 * @property {React.MutableRefObject<{[narrativeID: string]: React.MutableRefObject<boolean>}>} [narrativeEasterEggRefs] - References for tracking easter eggs
 * @property {function} [onEasterEggFound] - Callback when an easter egg is found
 */
interface ExtractedInfoPageProps {
  isFinalReview?: boolean;
  incidentModelRef?:
    | React.MutableRefObject<EforceIncidentModel | null>
    | undefined;
  narrativeEasterEggRefs?: React.MutableRefObject<{
    [narrativeID: string]: React.MutableRefObject<boolean>;
  }>;
  jsonSchema?: JSONSchema7;
  schemaMapping?: Record<string, FieldMapping>;
  onEasterEggFound?: () => void;
  stationId?: string;
}

/**
 * @name ExtractedInfoPage
 *
 * @description Component that displays extracted information from an incident report
 *
 * This component handles:
 * - Fetching and displaying extracted incident information
 * - Editing incident information
 * - Committing reports to RMS
 * - Downloading incident reports
 * - Displaying video previews
 *
 * @param {ExtractedInfoPageProps} props - Component props
 *
 * @returns {JSX.Element} The rendered component
 */
export const ExtractedInfoPage: React.FC<ExtractedInfoPageProps> = ({
  isFinalReview,
  incidentModelRef,
  narrativeEasterEggRefs,
  onEasterEggFound,
  jsonSchema: providedJsonSchema,
  schemaMapping: providedSchemaMapping,
  stationId,
}) => {
  const theme = useTheme();

  const [incident, setIncident] = useState<EforceIncidentModel | null>(null);

  // Report status states
  const [isReportSigned, setIsReportSigned] = useState<boolean>(false);
  const [isReportCommitted, setIsReportCommitted] = useState<boolean>(false);
  const [editable, setEditable] = useState<boolean>(false);
  const [reportStatus, setReportStatus] = useState<string>("");
  const permissions = usePermissions();
  const isCo = permissions.includes(REPORTS_MANAGE_STATION_PERMISSION);
  const coApprovalEnabled = useCoApprovalEnabled();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [videoData, setVideoData] = useState<VideoData | null | VideoData[]>(
    null,
  );
  const [jsonSchema, setJsonSchema] = useState<JSONSchema7 | null>(
    providedJsonSchema || null,
  );
  const [schemaMapping, setSchemaMapping] = useState<Record<
    string,
    FieldMapping
  > | null>(providedSchemaMapping || null);

  // UI control states
  const [isCommitting, setIsCommitting] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [showRMSCommit, setShowRMSCommit] = useState<boolean>(false);
  const [showDownloadLanguageModal, setShowDownloadLanguageModal] =
    useState(false);
  const [showTranscriptLanguageModal, setShowTranscriptLanguageModal] =
    useState(false);
  const [refsObject, setRefsObject] = useState<
    Record<string, React.RefObject<HTMLDivElement>>
  >({});
  const [rms, setRMS] = useState<string | null>(null);
  // Fatal load failure (report metadata/content or schema). Without this the
  // page rendered its skeleton forever when a fetch rejected.
  const [loadError, setLoadError] = useState<string | null>(null);
  // Bumped by the error panel's Try Again button to re-run the data effects.
  const [retryNonce, setRetryNonce] = useState(0);

  // Hooks for navigation, error handling, etc.
  const toast = useToast();
  const { reportID } = useParams();
  const navigate = useNavigate();

  // Show video preview if report is signed and video data exists
  const showVideoPreview = videoData && isReportSigned;

  /**
   * @name createSectionRefs
   *
   * @description Creates references for each section in the incident model
   *
   * @returns {Record<string, React.RefObject<HTMLDivElement>>} Object with section names as keys and refs as values
   */
  const createSectionRefs = useCallback(() => {
    const refs: { [key: string]: React.RefObject<HTMLDivElement> } = {};
    if (incident) {
      Object.entries(incident).forEach(([section, value]) => {
        if (Array.isArray(value)) {
          refs[section] = createRef<HTMLDivElement>();
        }
      });
    }
    return refs;
  }, [incident]);

  /**
   * Creates refs for each section of the incident for scrolling functionality
   *
   * @returns {Record<string, React.RefObject<HTMLDivElement>>} Object containing refs for each section
   */
  useEffect(() => {
    // Create refs for each section of the incident
    if (refsObject && Object.keys(refsObject).length === 0) {
      const refs = createSectionRefs();
      // Only set refsObject if refs contains keys; avoid setting empty objects which would
      // trigger re-renders and cause a maximum update depth exceeded error.
      if (refs && Object.keys(refs).length > 0) {
        setRefsObject(refs);
      }
    }
  }, [incident, createSectionRefs, refsObject]);

  /**
   * Fetches the extracted incident data from the API
   */
  const fetchExtractedIncident = useCallback(async () => {
    if (!reportID) return;

    try {
      const [metadataResult, incidentResult] = await Promise.allSettled([
        getReportMetadata(reportID),
        getIncidentData(reportID),
      ]);

      if (metadataResult.status === "fulfilled") {
        const metadata = metadataResult.value;
        setEditable(metadata.canEdit);
        setReportStatus(metadata.reportStatus);
        setIsReportSigned(
          SIGNED_REPORT_STATUSES.includes(
            metadata.reportStatus as ReportStatus,
          ),
        );
        setIsReportCommitted(metadata.reportStatus === ReportStatus.RMS_COMMITTED);
        setShowRMSCommit(![null, "", "KAR"].includes(metadata.rms));
        // Normalize like CoReviewPage and useReportData: null/empty rms means
        // the native KAR schema, so the KAR-only schema slicing below applies.
        setRMS(metadata.rms || "KAR");

        // Fetch evidence data using metadata evidenceIds
        Promise.all(metadata.evidenceIds.map((id) => getEvidenceData(id)))
          .then((evidenceResults) => {
            setVideoData(evidenceResults);
          })
          .catch((err) => {
            console.error("Error fetching evidence data:", err);
            toast.error(
              "An error occurred while fetching evidence data. Please try again later.",
            );
          });
      } else {
        throw metadataResult.reason;
      }

      if (incidentResult.status === "fulfilled") {
        setIncident(incidentResult.value.reportContent);
      } else {
        throw incidentResult.reason;
      }
    } catch (error: any) {
      setLoadError(
        error?.response?.data?.error ||
          "An error occurred while fetching report information. Please try again later.",
      );
    }
  }, [reportID, toast]);

  // Set page titles based on current view mode
  const pageTitle = isFinalReview ? "Final Review" : "Generated Report Review";

  const pageSubTitle = isFinalReview
    ? "Review the information once more before submitting"
    : "Review the extracted information from your video evidence";

  // Set page header in app bar (only when not used as child component)
  usePageHeader({
    title: pageTitle,
    subtitle: pageSubTitle,
    icon: <PreviewIcon />,
  });

  // Opens the download language selection modal
  const showDownloadModal = () => {
    setShowDownloadLanguageModal(true);
  };

  // Closes the download language selection modal
  const handleCloseDownloadModal = () => {
    setShowDownloadLanguageModal(false);
  };

  /**
   * @name handleDownloadIncident
   *
   * @description Downloads the incident report in the selected language
   *
   * @param {string} selectedLanguageCode - The language code for the report
   *
   * @returns {Promise<void>} A promise that resolves when the download is complete
   */
  const handleDownloadIncident = (selectedLanguageCode: string) => {
    return rmsClient
      .get("/report/download", {
        params: { reportId: reportID, languageCode: selectedLanguageCode },
        responseType: "blob",
        headers: {
          Accept: "application/pdf",
        },
      })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: "application/pdf",
        });
        saveAs(blob, `incident-information-${reportID}.pdf`);
        setShowDownloadLanguageModal(false);
      })
      .catch((error) => {
        toast.error(
          error.response?.data?.error ||
            "An error occurred while downloading the document. Please try again later.",
        );
      });
  };

  // Fetch incident data or use provided reference
  useEffect(() => {
    if (!isFinalReview) {
      fetchExtractedIncident();
    } else if (incidentModelRef) {
      // set current incident and sparse info to edited versions
      setIncident(incidentModelRef.current);
    }
  }, [isFinalReview, fetchExtractedIncident, incidentModelRef, retryNonce]);

  // Navigate to edit section if conditions are met. Gate on the same status
  // whitelist as EditSection's useReportData (EDITABLE_STATUSES) — gating the
  // two pages on different fields (canEdit here, a status whitelist there)
  // caused an infinite redirect loop for statuses like AWAITING INPUT where
  // canEdit is true but the edit page bounces back here.
  useEffect(() => {
    if (
      editable &&
      !isReportSigned &&
      !isReportCommitted &&
      incident &&
      EDIT_REDIRECT_STATUSES.includes(reportStatus as ReportStatus)
    ) {
      navigate(`/edit-section/${reportID}`);
    }
  }, [
    editable,
    isReportSigned,
    isReportCommitted,
    incident,
    reportStatus,
    reportID,
    navigate,
  ]);

  useEffect(() => {
    // Fetch the json schema if not provided. Wait for report metadata (rms is
    // null until it resolves) so the closure has the real rms/incident —
    // fetching on mount raced metadata, nondeterministically skipping the KAR
    // slicing and applying the generic mapping to EF reports.
    if (rms && !jsonSchema && !schemaMapping) {
      rmsClient
        .get("/schema", {})
        .then((response) =>
          JsonRefs.resolveRefs(response.data.schema as JSONSchema7, {
            filter: "local",
          }),
        )
        .then((result) => {
          let resolvedSchema = result.resolved as JSONSchema7;
          if (incident && rms === "KAR") {
            // slice schema if KAR to only include sections present in incident data
            resolvedSchema = filterSchemaByData(incident, resolvedSchema);
          }
          setJsonSchema(resolvedSchema);
          switch (rms) {
            case "EF":
              setSchemaMapping(eforceSchemaMapping);
              break;
            default:
              setSchemaMapping(genericSchemaMapping);
              break;
          }
        })
        .catch((error) => {
          console.error("Failed to load report schema:", error);
          setLoadError(
            error?.response?.data?.error ||
              "An error occurred while loading the report form. Please try again.",
          );
        });
    }
  }, [rms, incident, jsonSchema, schemaMapping, retryNonce]);

  // Submits the incident report to the RMS system. Only invocable by COs after approval.
  const handleRMSCommit = async () => {
    if (isCommitting) return;
    setOpenConfirmation(false);
    if (!incident) {
      toast.error("No incident data available.");
      return;
    }
    setIsCommitting(true);
    try {
      const res = await rmsCommitReport(reportID!);
      navigate("/confirmation/" + (res.eventNumber ?? ""), {
        state: { reportID },
      });
    } catch (err: any) {
      console.error(err);
      toast.error(
        err?.response?.data?.error ||
          "An error occurred while submitting the report to the RMS servers. Please try again later.",
      );
    } finally {
      setIsCommitting(false);
    }
  };

  // Fatal load failure: show an error state with retry instead of an
  // indefinite skeleton.
  if (loadError && (!incident || !jsonSchema || !schemaMapping)) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: "center" }}>
        <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
          {loadError}
        </Alert>
        <Button
          variant="contained"
          onClick={() => {
            setLoadError(null);
            setRetryNonce((n) => n + 1);
          }}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Try Again
        </Button>
      </Container>
    );
  }

  // Renders a loading skeleton when incident data is not yet available
  if (!incident || !jsonSchema || !schemaMapping) {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: { xs: 1, sm: 2, md: 3 },
            width: "75vw",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid size={{ xs: 12 }}>
              {/* Button */}
              <Box
                display="flex"
                justifyContent="center"
                width="100%"
                sx={{ my: 2 }}
              >
                <Skeleton width="20%" height={100} />
              </Box>
            </Grid>
          </Grid>
          <ExtractedInformationSkeleton />
        </Box>
      </>
    );
  }

  return (
    <>
      <ReportSubmitModal
        open={openConfirmation}
        handleClose={() => setOpenConfirmation(false)}
        handleSubmitReport={handleRMSCommit}
        isRMSCommit
      />
      <DownloadReportModal
        showDownloadLanguageModal={showDownloadLanguageModal}
        handleCloseDownloadModal={handleCloseDownloadModal}
        handleDownloadIncident={handleDownloadIncident}
      />
      <DownloadTranscriptLanguageModal
        open={showTranscriptLanguageModal}
        onClose={() => setShowTranscriptLanguageModal(false)}
        onDownload={async (languageCode) => {
          try {
            await downloadCombinedTranscriptInLanguage(
              Array.isArray(videoData)
                ? videoData
                : videoData
                  ? [videoData]
                  : null,
              `transcript-${reportID ?? "report"}`,
              languageCode,
            );
            setShowTranscriptLanguageModal(false);
          } catch (error) {
            console.error("Failed to download transcript:", error);
            toast.error("Failed to translate the transcript. Please try again.");
          }
        }}
      />
      {/* Action Buttons and Video Preview - only show when not used as child component */}
      {!isFinalReview && (
        <Container maxWidth="xl" sx={{ pt: 2, pb: 1 }}>
          {coApprovalEnabled &&
            REVIEW_HISTORY_VISIBLE_STATUSES.includes(
              reportStatus as ReportStatus,
            ) && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => setHistoryOpen(true)}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              >
                Review history
              </Button>
            </Box>
          )}
          {/* Action Buttons */}
          {!editable && isReportSigned && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                onClick={showDownloadModal}
                startIcon={<Download />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1.2,
                  boxShadow: `0 4px 14px ${alpha(
                    theme.palette.primary.main,
                    0.3,
                  )}`,
                  "&:hover": {
                    boxShadow: `0 6px 20px ${alpha(
                      theme.palette.primary.main,
                      0.4,
                    )}`,
                  },
                }}
              >
                Download Document
              </Button>
              {hasAnyTranscript(
                Array.isArray(videoData)
                  ? videoData
                  : videoData
                    ? [videoData]
                    : null,
              ) && (
                <Button
                  variant="outlined"
                  startIcon={<SubtitlesIcon />}
                  onClick={() => setShowTranscriptLanguageModal(true)}
                  sx={{
                    ml: 2,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    py: 1.2,
                  }}
                >
                  Download Transcript
                </Button>
              )}
              {!isReportCommitted &&
                showRMSCommit &&
                (coApprovalEnabled
                  ? isCo && reportStatus === ReportStatus.CO_APPROVED
                  : true) && (
                  <Button
                    onClick={() => setOpenConfirmation(true)}
                    variant="contained"
                    disabled={isCommitting || !incident}
                    loading={isCommitting}
                    sx={{
                      ml: 2,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                      py: 1.2,
                      boxShadow: `0 4px 14px ${alpha(
                        theme.palette.primary.main,
                        0.3,
                      )}`,
                      "&:hover": {
                        boxShadow: `0 6px 20px ${alpha(
                          theme.palette.primary.main,
                          0.4,
                        )}`,
                      },
                    }}
                  >
                    COMMIT DATA TO RMS
                  </Button>
                )}
            </Box>
          )}

          {showVideoPreview && (
            <VideoPreview videoData={videoData} isMinimizable={false} />
          )}
        </Container>
      )}

      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <ExtractedInformation
              formData={incident}
              jsonSchema={jsonSchema}
              schemaMapping={schemaMapping}
              title="Extracted Information"
              narrativeEasterEggRefs={narrativeEasterEggRefs}
              onEasterEggFound={onEasterEggFound}
              stationId={stationId}
            />
          </Grid>
        </Grid>
      </Container>
      {reportID && coApprovalEnabled && (
        <ReviewHistoryDialog
          open={historyOpen}
          reportId={reportID}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </>
  );
};
